import { useMemo } from 'react';
import useAuthStore from '../stores/authStore';
import { UsePermissionsReturn, Permission, Role } from '../types/auth';

export const usePermissions = (): UsePermissionsReturn => {
  const { user, hasPermission, hasRole, isAdmin } = useAuthStore();
  
  return useMemo(() => ({
    // Verificação de permissão única
    hasPermission: (permission: string) => hasPermission(permission),
    
    // Verificação de qualquer uma das permissões
    hasAnyPermission: (permissions: string[]) => 
      permissions.some(permission => hasPermission(permission)),
    
    // Verificação de todas as permissões
    hasAllPermissions: (permissions: string[]) =>
      permissions.every(permission => hasPermission(permission)),
    
    // Verificação de role único
    hasRole: (role: string) => hasRole(role),
    
    // Verificação de qualquer um dos roles
    hasAnyRole: (roles: string[]) =>
      roles.some(role => hasRole(role)),
    
    // Estados de permissão úteis
    isAdmin: isAdmin(),
    isSuperuser: user?.is_superuser ?? false,
    isStaff: user?.is_staff ?? false,
    
    // Listas de permissões e roles
    permissions: user?.permissions ?? [],
    roles: user?.groups ?? [],
  }), [user, hasPermission, hasRole, isAdmin]);
};

// Hook específico para verificar permissões de produto
export const useProductPermissions = () => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  return useMemo(() => ({
    canView: hasPermission(Permission.PRODUCT_VIEW),
    canCreate: hasPermission(Permission.PRODUCT_CREATE),
    canEdit: hasPermission(Permission.PRODUCT_EDIT),
    canDelete: hasPermission(Permission.PRODUCT_DELETE),
    canSync: hasPermission(Permission.PRODUCT_SYNC),
    canManage: hasAnyPermission([
      Permission.PRODUCT_CREATE,
      Permission.PRODUCT_EDIT,
      Permission.PRODUCT_DELETE
    ]),
    hasAnyAccess: hasAnyPermission([
      Permission.PRODUCT_VIEW,
      Permission.PRODUCT_CREATE,
      Permission.PRODUCT_EDIT,
      Permission.PRODUCT_DELETE,
      Permission.PRODUCT_SYNC
    ]),
  }), [hasPermission, hasAnyPermission]);
};

// Hook específico para verificar permissões de pedido
export const useOrderPermissions = () => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  return useMemo(() => ({
    canView: hasPermission(Permission.ORDER_VIEW),
    canEdit: hasPermission(Permission.ORDER_EDIT),
    canDelete: hasPermission(Permission.ORDER_DELETE),
    canSync: hasPermission(Permission.ORDER_SYNC),
    canManage: hasAnyPermission([
      Permission.ORDER_EDIT,
      Permission.ORDER_DELETE
    ]),
    hasAnyAccess: hasAnyPermission([
      Permission.ORDER_VIEW,
      Permission.ORDER_EDIT,
      Permission.ORDER_DELETE,
      Permission.ORDER_SYNC
    ]),
  }), [hasPermission, hasAnyPermission]);
};

// Hook específico para verificar permissões de sincronização
export const useSyncPermissions = () => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  
  return useMemo(() => ({
    canView: hasPermission(Permission.SYNC_VIEW),
    canExecute: hasPermission(Permission.SYNC_EXECUTE),
    canConfigure: hasPermission(Permission.SYNC_CONFIGURE),
    canManage: hasAnyPermission([
      Permission.SYNC_EXECUTE,
      Permission.SYNC_CONFIGURE
    ]),
    hasAnyAccess: hasAnyPermission([
      Permission.SYNC_VIEW,
      Permission.SYNC_EXECUTE,
      Permission.SYNC_CONFIGURE
    ]),
  }), [hasPermission, hasAnyPermission]);
};

// Hook específico para verificar permissões de admin
export const useAdminPermissions = () => {
  const { hasPermission, hasAnyPermission, isAdmin, isSuperuser } = usePermissions();
  
  return useMemo(() => ({
    hasAccess: isAdmin,
    canManageUsers: hasPermission(Permission.USER_MANAGE),
    canConfigureSystem: hasPermission(Permission.SYSTEM_CONFIG),
    canViewLogs: hasPermission(Permission.LOGS_VIEW),
    canViewAudit: hasPermission(Permission.AUDIT_VIEW),
    isSuperuser,
    hasAnyAdminPermission: hasAnyPermission([
      Permission.ADMIN_ACCESS,
      Permission.USER_MANAGE,
      Permission.SYSTEM_CONFIG,
      Permission.LOGS_VIEW,
      Permission.AUDIT_VIEW
    ]),
  }), [hasPermission, hasAnyPermission, isAdmin, isSuperuser]);
};

// Hook para verificar se pode acessar uma rota específica
export const useCanAccessRoute = (
  permissions?: string[],
  roles?: string[],
  requireAuth = true
) => {
  const { hasAnyPermission, hasAnyRole, isAdmin } = usePermissions();
  const { isAuthenticated } = useAuthStore();
  
  return useMemo(() => {
    // Se não requer autenticação, sempre pode acessar
    if (!requireAuth) return true;
    
    // Se requer auth mas não está logado
    if (requireAuth && !isAuthenticated) return false;
    
    // Se é admin, pode acessar tudo
    if (isAdmin) return true;
    
    // Verificar permissões específicas
    if (permissions && permissions.length > 0) {
      if (!hasAnyPermission(permissions)) return false;
    }
    
    // Verificar roles específicos
    if (roles && roles.length > 0) {
      if (!hasAnyRole(roles)) return false;
    }
    
    return true;
  }, [permissions, roles, requireAuth, isAuthenticated, hasAnyPermission, hasAnyRole, isAdmin]);
};

export default usePermissions;